import { z } from "zod";
import { DocumentType } from "@/types/form";
import { invoiceItemSchema } from "./schema";

// Common schemas as base building blocks
// const referenceDocumentSchema = z.object({
// 	documentNumber: z.string().min(1, "Reference document number is required"),
// 	documentType: z.nativeEnum(DocumentType),
// 	documentDate: z.string(),
// 	originalAmount: z.number().positive(),
// });

const bankDetailsSchema = z.object({
  bankName: z.string().min(1, "Bank name is required"),
  accountNumber: z.string().min(1, "Account number is required"),
  accountHolder: z.string().min(1, "Account holder name is required"),
});

const shippingRecipientSchema = z.object({
  id: z.string().uuid().optional(), // keep optional for id
  recipientName: z
    .string()
    .transform((val) => (val.trim() === "" ? null : val))
    .nullable(),
  recipientTin: z
    .string()
    .max(14)
    .transform((val) => (val.trim() === "" ? null : val))
    .nullable(),
  recipientRegistrationType: z
    .string()
    .max(20)
    .transform((val) => (val.trim() === "" ? null : val))
    .nullable()
    .default(null),
  recipientRegistrationNumber: z
    .string()
    .max(20)
    .transform((val) => (val.trim() === "" ? null : val))
    .nullable(),
  recipientAddressLine0: z
    .string()
    .max(150)
    .transform((val) => (val.trim() === "" ? null : val))
    .nullable(),
  recipientAddressLine1: z
    .string()
    .max(150)
    .transform((val) => (val.trim() === "" ? null : val))
    .nullable(),
  recipientAddressLine2: z
    .string()
    .max(150)
    .transform((val) => (val.trim() === "" ? null : val))
    .nullable(),
  recipientCity: z
    .string()
    .max(50)
    .transform((val) => (val.trim() === "" ? null : val))
    .nullable(),
  recipientPostcode: z
    .string()
    .transform((val) => (val.trim() === "" ? null : val))
    .nullable()
    .refine((val) => val === null || val.length === 5, {
      message: "Postcode must be exactly 5 characters or null",
    }),
  recipientState: z
    .string()
    .length(2)
    .transform((val) => (val.trim() === "" ? null : val))
    .nullable()
    .default(null),
  recipientCountry: z
    .string()
    .length(3)
    .transform((val) => (val.trim() === "" ? null : val))
    .nullable()
    .default(null),
});

const customsDetailsSchema = z.object({
  id: z.string().uuid().optional(),
  formReferenceNumber1: z
    .string()
    .max(150)
    .transform((val) => (val.trim() === "" ? null : val))
    .nullable(),
  incoterms: z
    .string()
    .transform((val) => (val.trim() === "" ? null : val))
    .nullable()
    .refine((val) => val === null || val.length === 3, {
      message: "Incoterms must be exactly 3 characters or null",
    }),
  ftaInformation: z
    .string()
    .transform((val) => (val.trim() === "" ? null : val))
    .nullable(),
  authorisationNumberForCertifiedExporter: z
    .string()
    .transform((val) => (val.trim() === "" ? null : val))
    .nullable(),
  formReferenceNumber2: z
    .string()
    .transform((val) => (val.trim() === "" ? null : val))
    .nullable(),
  detailsOfOtherCharges: z
    .string()
    .transform((val) => (val.trim() === "" ? null : val))
    .nullable(),
});

export type ShippingDetails = z.infer<typeof shippingRecipientSchema>;
export type CustomsDetails = z.infer<typeof customsDetailsSchema>;

const emailSchema = z
  .string({ message: "Email is required" })
  .min(1, { message: "Email cannot be empty" })
  .refine(
    (value) => {
      if (value === "NA") return true;

      return z.string().email().safeParse(value).success;
    },
    {
      message: "Only valid email address or NA is allowed",
    },
  );

const baseDocumentSchema = z.object({
  // buyer information
  buyerName: z.string().min(1, { message: "Name is required" }),
  buyerTin: z.string().min(1, { message: "TIN is required" }),
  buyerRegistrationType: z
    .string({ message: "Registration type is required" })
    .min(1, { message: "Registration type is required" }),
  buyerRegistrationNumber: z
    .string()
    .min(1, { message: "Registration no. is required" }),
  buyerSstRegistrationNumber: z.string().nullable(),
  buyerEmail: emailSchema.nullable(),
  buyerContact: z
    .string()
    .min(1, { message: "Buyer contact number is required" })
    .regex(/^\+?\d+$/, {
      message:
        "Contact number must contain only digits with an optional plus (+) symbol at the start and no spaces",
    })
    .min(8, { message: "Contact number must be at least 8 characters" })
    .max(20, { message: "Contact number must not exceed 20 characters" })
    .or(z.literal("NA")), // allow "NA"
  buyerAddressLine0: z
    .string()
    .min(1, { message: "Address is required" })
    .max(150, {
      message:
        "Address Line 1 must not exceed 150 characters — continue in Address Line 2/3",
    }),
  buyerAddressLine1: z
    .string()
    .max(150, { message: "Address Line 2 must not exceed 150 characters" })
    .nullable(),
  buyerAddressLine2: z
    .string()
    .max(150, { message: "Address Line 3 must not exceed 150 characters" })
    .nullable(),
  buyerCity: z.string().min(1, { message: "City is required" }),
  buyerPostcode: z.string().nullable(),
  buyerCountry: z
    .string({ message: "Country is required" })
    .min(1, { message: "Country is required" }),
  buyerState: z
    .string({ message: "State is required" })
    .min(1, { message: "State is required" }),

  // supplier information
  supplierName: z.string(),
  supplierTin: z.string(),
  supplierRegistrationType: z.string(),
  supplierRegistrationNumber: z.string(),
  supplierSstRegistrationNumber: z.string().nullable(),
  supplierBusinessActivityDescription: z
    .string()
    .max(300, { message: "Maximum 300 characters only" }),
  supplierTourismTaxRegistrationNumber: z.string().nullable(),
  supplierMsicCode: z.string(),
  supplierEmail: emailSchema.nullable(),
  supplierContact: z.string(),
  supplierAddressLine0: z.string().max(150, {
    message:
      "Address Line 1 must not exceed 150 characters — continue in Address Line 2/3",
  }),
  supplierAddressLine1: z
    .string()
    .max(150, { message: "Address Line 2 must not exceed 150 characters" })
    .nullable(),
  supplierAddressLine2: z
    .string()
    .max(150, { message: "Address Line 3 must not exceed 150 characters" })
    .nullable(),
  supplierCity: z.string(),
  supplierPostcode: z.string().nullable(),
  supplierCountry: z.string(),
  supplierState: z.string(),

  // Invoice details
  documentType: z.nativeEnum(DocumentType), // e-invoice type code (LHDN)
  codeNumber: z
    .string()
    .min(1, { message: "Invoice number is required" })
    .max(50, { message: "Maximum 50 characters only" }),
  invoiceDate: z.string(),
  invoiceTime: z.string(),
  issuerDigitalSignature: z.string(), // Issuer’s Digital Signature (LHDN)
  currencyCode: z.string().min(1, { message: "Currency code required" }),
  currencyExchangeRate: z.number().nullable().default(null),
  docDate: z.string().nullable().default(null),
  jobCode: z.string().nullable().default(null),
  originalInvoiceNumber: z
    .string()
    .max(50, { message: "Maximum 50 characters allowed" })
    .optional(),

  // Frequent of Billing Information
  frequencyOfBilling: z
    .string()
    .max(50, { message: "Maximum 50 characters only" })
    .nullable()
    .default(null),
  billingPeriodStartDate: z.string().nullable().default(null),
  billingPeriodEndDate: z.string().nullable().default(null),

  // LegalMonetaryTotal
  invoiceAdditionalDiscountAmount: z.number().nullable().default(null),
  invoiceAdditionalFeeAmount: z.number().nullable().default(null),
  roundingAmount: z.number().nullable().default(null),

  // invoice line item
  items: z
    .array(invoiceItemSchema, { message: "Items cannot be empty" })
    .min(1, { message: "Items cannot be empty" }), // Invoice Line Item (LHDN)

  // Payment and Prepayment Information
  paymentMode: z
    .enum(["01", "02", "03", "04", "05", "06", "07", "08"])
    .nullable()
    .default(null),
  supplierBankAccountNumber: z
    .string()
    .trim()
    .toLowerCase()
    .max(150, { message: "Maximum 150 characters only" })
    .nullable()
    .default(null), // .trim() and .toLowerCase() is for normalization
  paymentTerms: z
    .string()
    .max(300, { message: "Maximum 300 characters only" })
    .nullable()
    .default(null),
  prepaymentAmount: z.number().nullable().default(null),
  prepaymentDate: z.string().nullable().default(null),
  // prepaymentTime: ???? optional so ignore first
  prepaymentReferenceNumber: z
    .string()
    .max(150, { message: "Maximum 150 characters only" })
    .nullable()
    .default(null),
  billReferenceNumber: z.string().nullable().default(null), //only for payment related ref number

  // Shipping Information
  shipping: shippingRecipientSchema.optional(),
  // Import And Export Information
  customs: customsDetailsSchema.optional(),
});

const invoiceReference = z.object({
  id: z.string(),
  uuid: z.string(),
});

// Document type specific requirements
const documentTypeRequirements = {
  [DocumentType.CREDIT_NOTE]: {
    referenceDocument: true,
    reasonFields: false,
    refundFields: false,
    supplierApproval: false,
  },
  [DocumentType.DEBIT_NOTE]: {
    referenceDocument: true,
    reasonFields: false,
    refundFields: false,
    supplierApproval: false,
  },
  [DocumentType.SELF_BILLED_CREDIT_NOTE]: {
    referenceDocument: true,
    reasonFields: false,
    refundFields: false,
    supplierApproval: false,
  },
  [DocumentType.SELF_BILLED_DEBIT_NOTE]: {
    referenceDocument: true,
    reasonFields: false,
    refundFields: false,
    supplierApproval: false,
  },
  [DocumentType.REFUND_NOTE]: {
    referenceDocument: true,
    reasonFields: false,
    refundFields: false,
    supplierApproval: false,
  },
  [DocumentType.SELF_BILLED_REFUND_NOTE]: {
    referenceDocument: true,
    reasonFields: false,
    refundFields: false,
    supplierApproval: false,
  },
  [DocumentType.SELF_BILLED_INVOICE]: {
    referenceDocument: false,
    reasonFields: false,
    refundFields: false,
    supplierApproval: false,
  },
} as const;

export const getValidationSchema = (documentType: DocumentType) => {
  const requirements =
    documentTypeRequirements[
      documentType as keyof typeof documentTypeRequirements
    ] || {};

  let schemaExtension = {};

  if (requirements.referenceDocument) {
    schemaExtension = {
      ...schemaExtension,
      // referenceDocument: referenceDocumentSchema,
      originalInvoiceReference: z
        .array(invoiceReference, { message: "Reference is required" })
        .min(1, { message: "Reference is required" }),
    };
  }

  if (requirements.reasonFields) {
    schemaExtension = {
      ...schemaExtension,
      reasonCode: z.string().min(1, "Reason code is required"),
      reasonDescription: z.string().min(1, "Reason description is required"),
    };
  }

  if (requirements.refundFields) {
    schemaExtension = {
      ...schemaExtension,
      refundMethod: z.enum(["CASH", "BANK_TRANSFER", "CREDIT_CARD"]),
      bankDetails: bankDetailsSchema,
    };
  }

  if (requirements.supplierApproval) {
    schemaExtension = {
      ...schemaExtension,
      supplierApproval: z.boolean(),
    };
  }

  const extendedSchema = baseDocumentSchema.extend(schemaExtension);

  return extendedSchema.superRefine((data, ctx) => {
    if (data.buyerContact == "NA" && data.documentType != "01A") {
      ctx.addIssue({
        path: ["buyerContact"],
        code: z.ZodIssueCode.custom,
        message:
          "NA contact number is only allowed for consolidated invoice (01A)",
      });
    }

    // Supplier fields are ONLY rendered for self-billed types (11, 12, 13, 14)
    // For other types (01, 01A, 02, 03, 04)
    const isSelfBilledType = ["11", "12", "13", "14"].includes(
      data.documentType,
    );

    if (isSelfBilledType) {
      // Validate supplier fields only for self-billed types

      if (!data.supplierName || data.supplierName.trim().length === 0) {
        ctx.addIssue({
          path: ["supplierName"],
          code: z.ZodIssueCode.custom,
          message: "Name is required",
        });
      }

      if (!data.supplierTin || data.supplierTin.trim().length === 0) {
        ctx.addIssue({
          path: ["supplierTin"],
          code: z.ZodIssueCode.custom,
          message: "TIN is required",
        });
      }

      if (
        !data.supplierRegistrationType ||
        data.supplierRegistrationType.trim().length === 0
      ) {
        ctx.addIssue({
          path: ["supplierRegistrationType"],
          code: z.ZodIssueCode.custom,
          message: "Registration type is required",
        });
      }

      if (
        !data.supplierRegistrationNumber ||
        data.supplierRegistrationNumber.trim().length === 0
      ) {
        ctx.addIssue({
          path: ["supplierRegistrationNumber"],
          code: z.ZodIssueCode.custom,
          message: "Registration no. is required",
        });
      }

      if (
        !data.supplierBusinessActivityDescription ||
        data.supplierBusinessActivityDescription.trim().length === 0
      ) {
        ctx.addIssue({
          path: ["supplierBusinessActivityDescription"],
          code: z.ZodIssueCode.custom,
          message: "Supplier Business Activity Description is required",
        });
      }

      if (!data.supplierMsicCode || data.supplierMsicCode.trim().length === 0) {
        ctx.addIssue({
          path: ["supplierMsicCode"],
          code: z.ZodIssueCode.custom,
          message: "MSIC Code is required",
        });
      }

      if (!data.supplierContact || data.supplierContact.trim().length === 0) {
        ctx.addIssue({
          path: ["supplierContact"],
          code: z.ZodIssueCode.custom,
          message: "Supplier contact number is required",
        });
      } else {
        // Validate contact format
        if (!/^\+?\d+$/.test(data.supplierContact)) {
          ctx.addIssue({
            path: ["supplierContact"],
            code: z.ZodIssueCode.custom,
            message:
              "Contact number must contain only digits with an optional plus (+) symbol at the start and no spaces",
          });
        } else if (data.supplierContact.length < 8) {
          ctx.addIssue({
            path: ["supplierContact"],
            code: z.ZodIssueCode.custom,
            message: "Contact number must be at least 8 characters",
          });
        } else if (data.supplierContact.length > 20) {
          ctx.addIssue({
            path: ["supplierContact"],
            code: z.ZodIssueCode.custom,
            message: "Contact number must not exceed 20 characters",
          });
        }
      }

      if (
        !data.supplierAddressLine0 ||
        data.supplierAddressLine0.trim().length === 0
      ) {
        ctx.addIssue({
          path: ["supplierAddressLine0"],
          code: z.ZodIssueCode.custom,
          message: "Address is required",
        });
      }

      if (!data.supplierCity || data.supplierCity.trim().length === 0) {
        ctx.addIssue({
          path: ["supplierCity"],
          code: z.ZodIssueCode.custom,
          message: "City is required",
        });
      }

      if (!data.supplierCountry || data.supplierCountry.trim().length === 0) {
        ctx.addIssue({
          path: ["supplierCountry"],
          code: z.ZodIssueCode.custom,
          message: "Country is required",
        });
      }

      if (!data.supplierState || data.supplierState.trim().length === 0) {
        ctx.addIssue({
          path: ["supplierState"],
          code: z.ZodIssueCode.custom,
          message: "State is required",
        });
      }
    }
  });
};

export const documentSchema = baseDocumentSchema;
